import { useIntl } from "react-intl";

import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import {
  IconCarryOnBaggage,
  IconCarryOnBaggageNotIncluded,
  IconCheckedBaggage,
  IconCheckedBaggageNotIncluded,
  IconHandBag,
  IconHandBagNotIncluded,
} from "@/components/Icons";
import type {
  GetFlightAvailabilityTokenBaggageDetailsV2,
  GetFlightAvailabilityTokenDirectionalBaggageV2,
} from "@/types/codegen";

type BaggageIconProps = {
  baggageType: "checked" | "carryOn" | "handBag";
  disabled?: boolean;
};

const BaggageIcon = ({ baggageType, disabled }: BaggageIconProps) => {
  const IconComponent =
    baggageType === "carryOn"
      ? disabled
        ? IconCarryOnBaggageNotIncluded
        : IconCarryOnBaggage
      : baggageType === "handBag"
        ? disabled
          ? IconHandBagNotIncluded
          : IconHandBag
        : disabled
          ? IconCheckedBaggageNotIncluded
          : IconCheckedBaggage;

  return <IconComponent className="size-5 shrink-0" />;
};

type BaggageDetailsProps = {
  baggageType: "checked" | "carryOn" | "handBag";
  data: GetFlightAvailabilityTokenBaggageDetailsV2;
};

export const BaggageDetails = ({ baggageType, data }: BaggageDetailsProps) => {
  const intl = useIntl();

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-start gap-1">
        <BaggageIcon baggageType={baggageType} />

        <div>
          <div className="text-sm leading-5 font-medium">
            {baggageType === "carryOn"
              ? intl.formatMessage({ id: "flights.baggage.carryOnBaggage" })
              : baggageType === "handBag"
                ? intl.formatMessage({ id: "flights.baggage.handBag" })
                : intl.formatMessage({ id: "flights.baggage.checkedBaggage" })}
            {data.weightKg
              ? `: ${intl.formatMessage({ id: "flights.baggage.upToXKg" }, { kg: data.weightKg })}`
              : `: ${intl.formatMessage({ id: "flights.baggage.included" })}`}
          </div>

          {data.dimensionsCm ? (
            <div className="mt-0.5 text-text-disabled-secondary">
              {intl.formatMessage(
                { id: "flights.baggage.upToXCmInTotal" },
                {
                  cm: data.dimensionsCm,
                },
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="text-text-disabled-secondary">
        {data.numberOfPieces
          ? intl.formatMessage(
              { id: "flights.baggage.xPieces" },
              {
                count: data.numberOfPieces,
              },
            )
          : null}
      </div>
    </div>
  );
};

type NoBaggageDetailsProps = {
  baggageType: "checked" | "carryOn";
};

export const NoBaggageDetails = ({ baggageType }: NoBaggageDetailsProps) => {
  const intl = useIntl();

  return (
    <div className="flex items-center gap-1 text-sm font-medium">
      <BaggageIcon baggageType={baggageType} disabled />
      <div>
        {baggageType === "carryOn"
          ? intl.formatMessage({ id: "flights.baggage.noFreeCarryOnBaggage" })
          : intl.formatMessage({ id: "flights.baggage.noFreeCheckedBaggage" })}
      </div>
    </div>
  );
};

type Props = {
  baggageData?: GetFlightAvailabilityTokenDirectionalBaggageV2;
  isLoading?: boolean;
};

export const BaggageAllowance = ({ baggageData, isLoading }: Props) => {
  const intl = useIntl();

  return (
    <div className="section-card text-text-primary">
      {isLoading ? (
        <div className="flex w-full items-center gap-2 text-sm">
          <CircularProgress size={20} />
          {intl.formatMessage({
            id: "flights.baggage.retrievingBaggageDetails",
          })}
        </div>
      ) : (
        <>
          <h3 className="mb-4 text-base font-medium">
            {intl.formatMessage({ id: "flights.baggage.baggageAllowance" })}
          </h3>

          <ul className="space-y-2 *:not-last:border-b *:not-last:border-b-stroke-neutral-soft *:not-last:pb-2">
            {baggageData?.carryOn && baggageData.carryOn.length > 0 ? (
              baggageData.carryOn.map((carryOnBaggage, index) => (
                <li key={index}>
                  <BaggageDetails
                    baggageType={
                      carryOnBaggage.isSmallBag ? "handBag" : "carryOn"
                    }
                    data={carryOnBaggage}
                  />
                </li>
              ))
            ) : (
              <li>
                <NoBaggageDetails baggageType="carryOn" />
              </li>
            )}

            {baggageData?.baggage && baggageData.baggage.length > 0 ? (
              baggageData.baggage.map((checkedBaggage, index) => (
                <li key={index}>
                  <BaggageDetails baggageType="checked" data={checkedBaggage} />
                </li>
              ))
            ) : (
              <li>
                <NoBaggageDetails baggageType="checked" />
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};
